import { useCallback, useEffect, useRef, useState } from 'react';

const endpoint = (process.env.REACT_APP_FIREBASE_FUNCTION_URL?.trim() || 'https://us-central1-charging-station-bdcf4.cloudfunctions.net').replace(/\/+$/, '');
async function call(action, data) {
  const response = await fetch(`${endpoint}/foodManager`, {
    method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, data })
  });
  const result = await response.json();
  if (!response.ok || result.success !== true) throw new Error(result.error || 'Cart request failed');
  return result;
}
const normalize = item => ({ ...item, _id: item.id || item._id || item.cartId || item.orderId, quantity: Number(item.quantity) || 0, price: Number(item.price) || 0 });

export default function useFirebaseCart(storeId, tableNo, enabled = true) {
  const [cartItems, setCartItems] = useState([]);
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState('');
  const items = useRef([]);
  const queue = useRef(Promise.resolve());
  const readVersion = useRef(0);
  const scope = useRef(0);
  const locked = useRef(false);
  const revisions = useRef(new Map());
  const failures = useRef(new Map());
  const updateItems = useCallback(next => { items.current = next; setCartItems(next); }, []);

  const fetchOrders = useCallback(async () => {
    const generation = scope.current;
    const result = await call('getOrders', { storeId, tableno: tableNo });
    if (generation === scope.current) setConfirmedOrders((result.data || []).map(normalize));
  }, [storeId, tableNo]);

  const loadCart = useCallback(async () => {
    if (locked.current) return;
    const generation = scope.current;
    const version = ++readVersion.current;
    locked.current = true; setIsProcessing(true);
    try {
      await queue.current;
      const result = await call('getCartItems', { storeId, tableno: tableNo });
      if (generation !== scope.current || version !== readVersion.current) return;
      updateItems((result.data || []).filter(i => i.status === 'active').map(normalize));
      failures.current.clear(); setError('');
    } catch (e) { if (generation === scope.current) setError(e.message); }
    finally { if (generation === scope.current) { locked.current = false; setIsProcessing(false); } }
  }, [storeId, tableNo, updateItems]);

  useEffect(() => {
    scope.current += 1;
    locked.current = false;
    failures.current.clear(); revisions.current.clear();
    updateItems([]); setConfirmedOrders([]); setError('');
    if (enabled) {
      loadCart();
      fetchOrders().catch(e => setError(e.message));
    } else setIsProcessing(false);
    return () => { scope.current += 1; readVersion.current += 1; };
  }, [enabled, loadCart, fetchOrders, updateItems]);

  function changeItem(cartId, quantity) {
    if (locked.current) return;
    const previous = items.current.find(i => i._id === cartId);
    if (!previous) return;
    const generation = scope.current;
    const revision = (revisions.current.get(cartId) || 0) + 1;
    revisions.current.set(cartId, revision);
    readVersion.current += 1;
    updateItems(quantity > 0 ? items.current.map(i => i._id === cartId ? { ...i, quantity } : i) : items.current.filter(i => i._id !== cartId));
    
    queue.current = queue.current.then(async () => {
      try {
        await call('batchUpdateAndDelete', {
          storeId, tableno: tableNo,
          items: quantity > 0 ? [{ cartId, quantity }] : [],
          deletedIds: quantity > 0 ? [] : [cartId]
        });
        if (generation !== scope.current) return;
        failures.current.delete(cartId);
        if (!failures.current.size) setError('');
      } catch (e) {
        if (generation !== scope.current) return;
        failures.current.set(cartId, e.message); setError(e.message);
        if (revisions.current.get(cartId) === revision) {
          updateItems(items.current.some(i => i._id === cartId)
            ? items.current.map(i => i._id === cartId ? previous : i)
            : [...items.current, previous]);
        }
      }
    });
  }
  function changeQty(cartId, diff) {
    const item = items.current.find(i => i._id === cartId);
    if (item) changeItem(cartId, Math.max(0, item.quantity + diff));
  }
  const removeItem = cartId => changeItem(cartId, 0);

  async function confirmOrder(comment, customer = {}) {
    if (locked.current || !items.current.length) return false;
    locked.current = true; setIsProcessing(true); readVersion.current += 1;
    try {
      await queue.current;
      if (failures.current.size) throw new Error('Some cart changes could not be saved. Refresh the cart before confirming.');
      await call('confirmOrder', { storeId, tableno: tableNo, comment, ...customer });
      readVersion.current += 1; updateItems([]); setError('');
       
      try { await fetchOrders(); } catch { setError('Order placed. Refresh to load confirmed orders.'); }
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { locked.current = false; setIsProcessing(false); }
  }
  return { cartItems, confirmedOrders, isProcessing, error, loadCart, fetchOrders, changeQty, removeItem, confirmOrder,
    cartTotal: cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0),
    confirmedTotal: confirmedOrders.reduce((sum, i) => sum + i.price * i.quantity, 0) };
}
